/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import {
  useContext, useEffect, useRef,
} from 'react';
import appConfig from 'appConfig';

import { FormContext } from '../Form/Form';
import { IFormContext } from '../../interfaces/components/form.interface';

import { useModal } from '../../contexts/Modal';
import { IProps } from '../../interfaces/components/bluetoothVehicles.interface';
import pairBluetooth from '../../services/rivian-bluetooth';
import api from '../../services/api';
import { useFlashErrorText } from '../../contexts/FlashErrorText';
import errorHandler from '../../services/sentry';
import { SelectionsContext } from '../../contexts/Selections';
import VehicleCard from '../VehicleCard';
import { IVehicle } from '../../interfaces/components/vehicleCard.interface';

interface IVehiclesForm {
  pairedVehicles: {
    [key: string]: {
      value: boolean;
      error?: string;
    };
  };
}

interface IBluetoothCredentials {
  hmacSecret: string;
  vehicleId: string;
  phoneId: string;
}

const errorMessageToKeys = {
  'Bluetooth turned off': 'bluetoothTurnedOff',
  'Bluetooth permission not granted': 'bluetoothPermissionNotGranted',
  'Bluetooth not available': 'bluetoothNotAvailable',
  'Location services disabled': 'locationServiceNotEnabled',
};

const getErrorKey = (errorMessage: string): string => {
  return (errorMessageToKeys[errorMessage] as string) || errorMessage;
};

const BluetoothVehicles = ({
  statuses,
  errorMap,
  vehicles,
  setComponentTree,
}: IProps) => {
  const context = useContext<IFormContext | undefined>(FormContext);
  const selectionsContext = useContext(SelectionsContext);
  const { openModal, closeModal } = useModal();
  const { setFlashErrorText } = useFlashErrorText();
  const abortControllerRef = useRef<AbortController | null>(null);

  const startPairing = async (vehicle: IVehicle, credentials: IBluetoothCredentials) => {
    try {
      abortControllerRef.current = new AbortController();
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      await pairBluetooth({
        ...credentials,
        signal: abortControllerRef.current.signal,
      });
      openModal('verifyPairingStatus', {
        cancel: () => {
          if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            abortControllerRef.current = null;
            closeModal();
          }
        },
      });
      const { data } = await api.getComponents(
        `/vehicle/${vehicle.id}/bluetooth-pairing-status`,
        true,
        { signal: abortControllerRef.current.signal },
      );
      if (data.status === 'success') {
        context?.setFormState((formState: IVehiclesForm) => ({
          ...formState,
          pairedVehicles: {
            ...formState.pairedVehicles,
            [vehicle.id]: {
              value: true,
            },
          },
        }));
        setFlashErrorText('');
        closeModal();
      } else {
        throw new Error('pairingFailed');
      }
    } catch (err: any) {
      if (err.name === 'AlreadyBondedError' || err.message === 'pairingFailed') {
        openModal('bluetoothSettings', {
          openBluetoothSettings: () => {
            openModal('setupBluetooth');
          },
        });
        return;
      }
      if (err.name !== 'AbortError' && err.message !== 'canceled') {
        errorHandler(err);
        const message = errorMap[getErrorKey(err.message)] || errorMap.default;
        setFlashErrorText(message);
      }
      closeModal();
    }
  };

  const createBluetoothHandler = (vehicle: IVehicle) => {
    return async (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      try {
        selectionsContext?.setVehicleToPair(vehicle);

        setFlashErrorText('');
        const route = `/vehicle/${vehicle.id}/access-credentials/bluetooth`;
        if (appConfig.timedTokenEnabled) {
          await api.getSessionToken();
        }
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const { data }: { data: IBluetoothCredentials } = await api.getComponents(route);
        openModal('setupBluetooth', {
          cancel: () => {
            if (abortControllerRef.current) {
              // AbortError will trigger closeModal in startPairing
              abortControllerRef.current.abort();
              abortControllerRef.current = null;
            } else {
              closeModal();
            }
          },
          advanceToConnectToBluetooth: () => {
            openModal('connectToBluetooth');
            startPairing(vehicle, data).catch((err: any) => {
              errorHandler(err);
            });
          },
        });
      } catch (err: any) {
        closeModal();
        errorHandler(err);
        if (err.response && err.response.data) {
          if (err.response.data.flash) {
            setFlashErrorText(err.response.data.message);
          } else {
          // display page error
            setComponentTree(err.response.data);
          }
        }
      }
    };
  };

  useEffect(() => {
    return closeModal;
  }, []);

  return (context?.formState as IVehiclesForm).pairedVehicles ? vehicles.map((vehicle, i) => {
    const granted = Boolean(
      (context?.formState as IVehiclesForm)?.pairedVehicles[vehicle.id]?.value,
    );
    const chipAdditionaClassNames = granted ? 'granted' : 'required';
    const statusLabel = statuses[granted ? 'granted' : 'required'];
    const lastVehicle = i === vehicles.length - 1;
    return (
      <VehicleCard
        key={vehicle.id}
        disabled={Boolean(granted)}
        vehicle={vehicle}
        statusLabel={statusLabel}
        handleClick={createBluetoothHandler(vehicle)}
        chipAdditionaClassNames={chipAdditionaClassNames}
        additionalClassNames={lastVehicle ? 'm-b-xl' : ''}
      />
    );
  }) : null;
};

export default BluetoothVehicles;
